import { getBEUrlForAdmin, setBackendDomain } from "./management";
import jwtAuthAxios from "../auth/jwtAuth";

export const importDotykackaCatalog = async (
  locationCode,
  { onDownloadProgress } = {},
) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.get(
    `/integration/dotykacka/catalog/${locationCode}`,
    {
      ...(onDownloadProgress ? { onDownloadProgress } : {}),
    },
  );
  return data;
};

export const makeDotykackaRefreshToken = async (customerId, beDomain) => {
  const be = getBEUrlForAdmin(beDomain);
  jwtAuthAxios.defaults.baseURL = `${be}/`;
  const { data } = await jwtAuthAxios.get(
    `/integration/dotykacka/merchant/register/${customerId}`,
  );
  if (data?.code === 200) {
    return data;
  }
  return {
    error: true,
  };
};

/**
 *
 * @returns {Promise<any>}
 */
export const exportCustomerMenu = async () => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(`/api/v1/menu/export`);
  return data;
};

export const exportSmartkioskMenu = async () => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(`/api/v1/menu/smartkiosk/export`);
  return data;
};

/**
 * Triggers full OBERON synchronization for a customer
 * @param {string} customerCode - The customer code
 * @param {Object} options - Optional axios config options
 * @returns {Promise<any>}
 */
export const syncOberonAll = async (
  customerCode,
  { onDownloadProgress } = {},
) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(
    `/integration/oberon/sync-changes-full`,
    { customerCode },
    {
      ...(onDownloadProgress ? { onDownloadProgress } : {}),
    },
  );
  return data;
};

/**
 * Triggers delta (changes-only) OBERON synchronization for a customer
 * @param {string} customerCode - The customer code
 * @param {Object} options - Optional axios config options
 * @returns {Promise<any>}
 */
export const syncOberonDelta = async (
  customerCode,
  { onDownloadProgress } = {},
) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(
    `/integration/oberon/sync-changes-delta`,
    { customerCode },
    {
      ...(onDownloadProgress ? { onDownloadProgress } : {}),
    },
  );
  return data;
};

/**
 * Trigger an OBERON product sync via the on-premise DLA (DSOK-456).
 * The DLA fetches from local OBERON and pushes to the backend, so no public fixed IP.
 * @param {{ customerId?: number|string, connectionCode: string, mode?: "import"|"delta" }} params
 * @returns {Promise<any>}
 */
export const syncOberonViaDla = async ({
  customerId,
  connectionCode,
  mode = "import",
}) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(`/integration/oberon/sync-via-dla`, {
    customerId,
    connectionCode,
    mode,
  });
  return data;
};

/**
 * Get OBERON sync job status
 * @param {string} jobId - The job identifier
 * @returns {Promise<any>}
 */
export const getOberonSyncStatus = async (jobId) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.get(
    `/integration/oberon/sync-status/${jobId}`,
  );
  return data;
};

/**
 * Cancel OBERON sync job
 * @param {string} jobId - The job identifier
 * @returns {Promise<any>}
 */
export const cancelOberonSyncJob = async (jobId) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(
    `/integration/oberon/cancel-job/${jobId}`,
  );
  return data;
};

/**
 * WooCommerce (WordPress) — dry-run connection test for a customer.
 * Validates the per-customer Consumer Key/Secret + store URL and returns counts/sample
 * without writing anything. Uses the credentials already stored in dsokSettings.
 * @param {string|number} customerId
 * @returns {Promise<any>}
 */
export const testWooConnection = async (customerId, config) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(`/integration/woocommerce/test`, {
    customerId,
    ...(config ? { config } : {}),
  });
  return data;
};

/**
 * WooCommerce (WordPress) — start async product + category import for a customer.
 * @param {string|number} customerId
 * @returns {Promise<any>} job payload (contains jobId)
 */
export const importWooProducts = async (customerId, config) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.post(`/integration/woocommerce/import`, {
    customerId,
    ...(config ? { config } : {}),
  });
  return data;
};

/**
 * WooCommerce (WordPress) — poll import job status.
 * @param {string} jobId
 * @returns {Promise<any>}
 */
export const getWooSyncStatus = async (jobId) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.get(
    `/integration/woocommerce/sync-status/${jobId}`,
  );
  return data;
};

/**
 * Get integration jobs for a customer
 * @param {string|number} customerId - The customer identifier
 * @param {Object} options - Query parameters
 * @param {string} [options.status] - Filter by status (done, running, failed, pending, canceled)
 * @param {string} [options.integration] - Filter by integration type
 * @param {string} [options.type] - Filter by job type
 * @param {string} [options.dateFrom] - Filter by date from (ISO date string)
 * @param {string} [options.dateTo] - Filter by date to (ISO date string)
 * @param {number} [options.limit] - Number of results to return
 * @param {number} [options.offset] - Offset for pagination
 * @returns {Promise<any>}
 */
export const getIntegrationJobs = async (
  customerId,
  { status, integration, type, dateFrom, dateTo, limit, offset } = {},
) => {
  setBackendDomain(jwtAuthAxios);

  // Build query params dynamically (only include defined values)
  const params = {};
  if (status !== undefined && status !== null && status !== "")
    params.status = status;
  if (integration !== undefined && integration !== null && integration !== "")
    params.integration = integration;
  if (type !== undefined && type !== null && type !== "")
    params.type = type;
  if (dateFrom !== undefined && dateFrom !== null && dateFrom !== "")
    params.dateFrom = dateFrom;
  if (dateTo !== undefined && dateTo !== null && dateTo !== "")
    params.dateTo = dateTo;
  if (limit !== undefined && limit !== null) params.limit = limit;
  if (offset !== undefined && offset !== null) params.offset = offset;

  const { data } = await jwtAuthAxios.get(
    `/integration/common/jobs/${customerId}`,
    { params },
  );
  return data;
};
